import clsx from "clsx";
import React, { useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import Slider from "react-slick";
import CloseButton from "web/react/components/buttons/close-button/close-button";
import "web/react/components/product-gallery/slick-theme.less";
import "web/react/components/product-gallery/slick.less";
import SVGIcon from "web/react/components/svg-icon/svg-icon";

import {
    Image,
    ProductGalleryImage,
} from "web/react/pages/product/product-image-gallery/product-gallery-image";
import { getSafariMajorVersion } from "web/react/utils/user-agent";
import { gettext } from "web/script/modules/django-i18n";
import * as styles from "./product-image-gallery-fullscreen.css";
import { ThumbnailStrip } from "./thumbnail-strip/thumbnail-strip";

interface ProductImageGalleryFullscreenProps {
    images: Image[];
    initialSlideIndex?: number;
    onClose: () => void;
}

interface ArrowButtonProps {
    variant: "prev" | "next";
    className?: string;
    onClick?: React.MouseEventHandler<HTMLButtonElement>;
    onKeyDown?: React.KeyboardEventHandler<HTMLButtonElement>;
    isMobile?: boolean;
    disabled?: boolean;
}

function ArrowButton({
    variant,
    className,
    onClick,
    onKeyDown,
    isMobile = false,
    disabled = false,
}: ArrowButtonProps): React.ReactElement {
    const rotation = variant === "prev" ? 90 : -90;
    const ariaLabel =
        variant === "prev"
            ? gettext("product_image_gallery_fullscreen.previous_image")
            : gettext("product_image_gallery_fullscreen.next_image");
    const baseStyle = isMobile
        ? styles.mobileNavigationArrow
        : variant === "prev"
        ? styles.prevArrow
        : styles.nextArrow;

    return (
        <button
            className={clsx(baseStyle, className)}
            onClick={onClick}
            onKeyDown={onKeyDown}
            aria-label={ariaLabel}
            disabled={disabled}
            tabIndex={0}
        >
            <SVGIcon name="chevron-thin" rotate={rotation} className={styles.arrowIcon} />
        </button>
    );
}

function PrevArrow(props: Omit<ArrowButtonProps, "variant">): React.ReactElement {
    return <ArrowButton {...props} variant="prev" />;
}

function NextArrow(props: Omit<ArrowButtonProps, "variant">): React.ReactElement {
    return <ArrowButton {...props} variant="next" />;
}

export function ProductImageGalleryFullscreen({
    images,
    initialSlideIndex = 0,
    onClose,
}: ProductImageGalleryFullscreenProps): React.ReactElement {
    const isIOS18 = getSafariMajorVersion() === 18;
    const dialogRef = useRef<HTMLDialogElement | null>(null);
    const sliderRef = useRef<Slider | null>(null);
    const [currentSlideIndex, setCurrentSlideIndex] = useState(initialSlideIndex);

    const canGoToPrevious = currentSlideIndex > 0;
    const canGoToNext = currentSlideIndex < images.length - 1;

    const handlePreviousClick = (): void => {
        if (canGoToPrevious) {
            sliderRef.current?.slickPrev();
        }
    };

    const handleNextClick = (): void => {
        if (canGoToNext) {
            sliderRef.current?.slickNext();
        }
    };

    useEffect(() => {
        const dialog = dialogRef.current;
        if (dialog) {
            dialog.showModal();
            dialog.focus();

            const handleCloseOnBackdrop = (e: MouseEvent): void => {
                if (e.target === dialog) {
                    onClose();
                }
            };

            dialog.addEventListener("click", handleCloseOnBackdrop);
            return () => dialog.removeEventListener("click", handleCloseOnBackdrop);
        }
    }, [onClose]);

    return ReactDOM.createPortal(
        <dialog
            ref={dialogRef}
            data-testid="fullscreen-gallery"
            onCancel={onClose}
            className={styles.overlay}
            tabIndex={-1}
        >
            <span className={styles.closeButton}>
                <CloseButton onClick={onClose} />
            </span>
            <Slider
                ref={sliderRef}
                speed={400}
                slidesToShow={1}
                centerMode={false}
                infinite={false}
                arrows
                swipe
                touchMove
                draggable={false}
                dots={false}
                beforeChange={(_, newIndex) => setCurrentSlideIndex(newIndex)}
                prevArrow={<PrevArrow />}
                nextArrow={<NextArrow />}
                className={styles.slider}
                initialSlide={initialSlideIndex}
            >
                {images.map((image, index) => (
                    <ProductGalleryImage
                        key={`${image.id}-${index}`}
                        src={image.url}
                        alt={image.altText ?? undefined}
                        lazy={!isIOS18}
                        isFullscreen
                        blurImageOnLoad={!isIOS18}
                        tabIndex={-1}
                    />
                ))}
            </Slider>
            <div className={styles.mobileThumbnailNavigation}>
                <PrevArrow onClick={handlePreviousClick} disabled={!canGoToPrevious} isMobile />
                <ThumbnailStrip
                    images={images}
                    currentSlideIndex={currentSlideIndex}
                    onThumbnailImageClick={(index) => sliderRef.current?.slickGoTo(index)}
                    isMobileInline
                />
                <NextArrow onClick={handleNextClick} disabled={!canGoToNext} isMobile />
            </div>
            <div className={styles.desktopThumbnailNavigation}>
                <ThumbnailStrip
                    images={images}
                    currentSlideIndex={currentSlideIndex}
                    onThumbnailImageClick={(index) => sliderRef.current?.slickGoTo(index)}
                />
            </div>
        </dialog>,
        document.body
    );
}
